import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { PageHeader, Empty, fmtDate } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  PaperPlaneTilt, EnvelopeSimple, ArrowClockwise, Trash, CheckCircle, XCircle,
  Paperclip, Users, Copy, Translate, DownloadSimple, Warning,
} from "@phosphor-icons/react";

const SERVICE_LABEL = { interpretation: "Interpretation", translation: "Translation", both: "Both" };
const SERVICE_TONE = {
  interpretation: "bg-blue-50 text-blue-700 border-blue-200",
  translation: "bg-purple-50 text-purple-700 border-purple-200",
  both: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const LANG_LABEL = { en: "English", es: "Español", fr: "Français", ht: "Kreyòl" };

export default function PendingClients() {
  const qc = useQueryClient();

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteService, setInviteService] = useState("both");
  const [inviteLang, setInviteLang] = useState("en");
  const [inviteNote, setInviteNote] = useState("");

  const [reviewing, setReviewing] = useState(null); // pending client under review

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ["pending-clients"],
    queryFn: async () => (await api.get("/public/pending-clients")).data,
  });
  const { data: invites = [] } = useQuery({
    queryKey: ["client-invites"],
    queryFn: async () => (await api.get("/public/client-invites")).data,
  });

  const invite = useMutation({
    mutationFn: async () => (await api.post("/public/client-invites", {
      email: inviteEmail.trim(),
      name: inviteName.trim() || null,
      service_type: inviteService,
      language: inviteLang,
      note: inviteNote.trim() || null,
      origin_url: window.location.origin,
    })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["client-invites"] });
      setInviteEmail(""); setInviteName(""); setInviteNote("");
      if (d.email_disabled) toast.warning("Invite saved — email service isn't configured, so no email was sent");
      else toast.success(d.resend_count > 0 ? "Invitation re-sent" : "Invitation sent");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });

  const resendInvite = useMutation({
    mutationFn: async (id) => (await api.post(`/public/client-invites/${id}/resend`, {})).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client-invites"] }); toast.success("Re-sent"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Resend failed"),
  });
  const revokeInvite = useMutation({
    mutationFn: async (id) => (await api.delete(`/public/client-invites/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["client-invites"] }); toast.success("Revoked"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Revoke failed"),
  });

  const approve = useMutation({
    mutationFn: async (client_id) => (await api.post(`/public/pending-clients/${client_id}/approve`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending-clients"] });
      qc.invalidateQueries({ queryKey: ["clients"] });
      setReviewing(null);
      toast.success("Client approved and added to the roster");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Approve failed"),
  });
  const reject = useMutation({
    mutationFn: async (client_id) => (await api.post(`/public/pending-clients/${client_id}/reject`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pending-clients"] });
      setReviewing(null);
      toast.success("Client rejected");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reject failed"),
  });

  const publicUrl = `${window.location.origin}/client-intake`;
  const copyPublic = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Public intake link copied"); }
    catch { toast.error("Copy failed"); }
  };

  const stats = useMemo(() => ({
    pending: pending.length,
    invites_sent: invites.filter((i) => i.status === "sent").length,
    invites_submitted: invites.filter((i) => i.status === "submitted").length,
  }), [pending, invites]);

  return (
    <div data-testid="pending-clients-page">
      <PageHeader
        eyebrow="Client onboarding"
        title="Pending clients"
        description="Invite clients by email in their preferred language, or share the public intake link. Review submissions here before adding them to your roster."
      />

      <div className="grid grid-cols-3 gap-3 mb-5">
        <StatCard label="Awaiting review" value={stats.pending} tone={stats.pending > 0 ? "amber" : "slate"} />
        <StatCard label="Invites sent" value={stats.invites_sent} />
        <StatCard label="Invites completed" value={stats.invites_submitted} tone="emerald" />
      </div>

      {/* Invite card */}
      <div className="card-flat p-5 mb-6" data-testid="client-invite-card">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#B91C1C] font-semibold">Direct outreach</div>
            <div className="font-display text-xl font-bold text-slate-900 mt-1">Invite a client by email</div>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              We'll send a branded email with a personalized intake link — pre-set to their service type and in the language you pick.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={copyPublic} data-testid="copy-public-link">
            <Copy size={14} className="mr-1.5" /> Copy public intake link
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Email <span className="text-red-500">*</span></label>
            <Input type="email" placeholder="client@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} data-testid="client-invite-email" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Name (optional)</label>
            <Input placeholder="Marie Dupont" value={inviteName} onChange={(e) => setInviteName(e.target.value)} data-testid="client-invite-name" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Service type</label>
            <Select value={inviteService} onValueChange={setInviteService}>
              <SelectTrigger data-testid="client-invite-service"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="interpretation">Interpretation</SelectItem>
                <SelectItem value="translation">Translation</SelectItem>
                <SelectItem value="both">Both interpretation & translation</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1 flex items-center gap-1">
              <Translate size={11} /> Form language
            </label>
            <Select value={inviteLang} onValueChange={setInviteLang}>
              <SelectTrigger data-testid="client-invite-lang"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="ht">Kreyòl Ayisyen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mb-3">
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">Personal note (optional)</label>
          <Textarea rows={2} placeholder="e.g. Great meeting you at the family law seminar — looking forward to working together." value={inviteNote} onChange={(e) => setInviteNote(e.target.value)} data-testid="client-invite-note" />
        </div>
        <Button
          onClick={() => invite.mutate()}
          disabled={invite.isPending || !inviteEmail.trim()}
          className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
          data-testid="client-invite-send"
        >
          <PaperPlaneTilt size={14} className="mr-2" /> {invite.isPending ? "Sending…" : "Send invitation"}
        </Button>

        {invites.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-200">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Sent invitations · {invites.length}</div>
            <div className="space-y-2">
              {invites.map((iv) => (
                <div key={iv.invite_id} className="flex items-center justify-between p-2 rounded border border-slate-200 bg-slate-50/60">
                  <div className="flex items-center gap-2 min-w-0">
                    <EnvelopeSimple size={14} className="text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm text-slate-800 truncate font-mono">{iv.email}</div>
                      <div className="text-[11px] text-slate-500">
                        {SERVICE_LABEL[iv.service_type] || "Both"} · {LANG_LABEL[iv.language] || "English"} · sent {fmtDate(iv.last_sent_at)}
                        {iv.resend_count > 0 && <> · resent {iv.resend_count}×</>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {iv.status === "submitted"
                      ? <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Completed</span>
                      : <span className="badge-pill bg-blue-50 text-blue-700 border-blue-200 text-[10px]">Sent</span>}
                    {iv.status !== "submitted" && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => resendInvite.mutate(iv.invite_id)}>
                        <ArrowClockwise size={12} className="mr-1" /> Resend
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-500 hover:text-red-600"
                      onClick={() => { if (window.confirm(`Revoke invite for ${iv.email}?`)) revokeInvite.mutate(iv.invite_id); }}>
                      <Trash size={12} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pending applications list */}
      <div className="mb-3 flex items-center gap-2">
        <Users size={16} className="text-slate-400" />
        <h2 className="font-display text-lg font-bold text-slate-900">Awaiting review</h2>
        <span className="text-xs text-slate-500">({pending.length})</span>
      </div>

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : pending.length === 0 ? (
        <Empty title="No pending client intakes" hint="Invite a client above or share the public intake link — new submissions land here for your review." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pending.map((c) => (
            <div key={c.client_id} className="card-flat p-4" data-testid={`pending-client-${c.client_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.contact_name && `${c.contact_name} · `}{c.email}</div>
                </div>
                <span className={`badge-pill text-[10px] shrink-0 ${SERVICE_TONE[c.client_type] || SERVICE_TONE.both}`}>
                  {SERVICE_LABEL[c.client_type] || "Both"}
                </span>
              </div>
              <div className="mt-2 text-xs text-slate-600 space-y-0.5">
                <div><b>{c.org_type}</b> · {c.phone || "—"}</div>
                {c.client_type !== "translation" && c.intake_interpretation_languages && (
                  <div className="text-slate-500">🎙️ {c.intake_interpretation_languages}</div>
                )}
                {c.client_type !== "interpretation" && (c.intake_source_language || c.intake_target_language) && (
                  <div className="text-slate-500">📄 {c.intake_source_language || "—"} → {c.intake_target_language || "—"}</div>
                )}
                {c.intake_notes && <div className="text-slate-500 italic mt-1">"{c.intake_notes}"</div>}
                {(c.intake_files || []).length > 0 && (
                  <div className="text-slate-500 mt-1">
                    <Paperclip size={11} className="inline mr-1" /> {c.intake_files.length} file(s) attached
                  </div>
                )}
                <div className="text-slate-400 text-[10px] uppercase tracking-wider mt-1">
                  Submitted {fmtDate(c.created_at)} · in {LANG_LABEL[c.intake_language] || "English"}
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => setReviewing(c)} data-testid={`review-${c.client_id}`}>
                  Review
                </Button>
                <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approve.mutate(c.client_id)} data-testid={`approve-${c.client_id}`}>
                  <CheckCircle size={14} className="mr-1" /> Approve
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review dialog with full details + downloads */}
      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>{reviewing?.name}</DialogTitle>
          </DialogHeader>
          {reviewing && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4 text-xs">
                <Row label="Contact" value={reviewing.contact_name || "—"} />
                <Row label="Email" value={reviewing.email} />
                <Row label="Phone" value={reviewing.phone || "—"} />
                <Row label="Preferred lang" value={LANG_LABEL[reviewing.preferred_language] || "English"} />
                <Row label="Service" value={SERVICE_LABEL[reviewing.client_type]} />
                <Row label="Case type" value={reviewing.org_type} />
                <Row label="Address" value={reviewing.address} full />
              </div>
              {(reviewing.client_type === "interpretation" || reviewing.client_type === "both") && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Interpretation</div>
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs space-y-1">
                    <div>Languages: <b>{reviewing.intake_interpretation_languages || "—"}</b></div>
                    <div>Format: <b>{reviewing.intake_interpretation_format || "—"}</b></div>
                    <div>First assignment: <b>{reviewing.intake_first_assignment_date || "—"}</b></div>
                  </div>
                </div>
              )}
              {(reviewing.client_type === "translation" || reviewing.client_type === "both") && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Translation</div>
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-md text-xs space-y-1">
                    <div>Language pair: <b>{reviewing.intake_source_language || "—"} → {reviewing.intake_target_language || "—"}</b></div>
                    <div>Document type: <b>{reviewing.intake_document_type || "—"}</b></div>
                    <div>Volume estimate: <b>{reviewing.intake_volume_estimate || "—"}</b></div>
                  </div>
                </div>
              )}
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Billing</div>
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-xs space-y-1">
                  <div>Contact: <b>{reviewing.billing_contact || "—"}</b></div>
                  <div>Method: <b>{reviewing.billing_method || "—"}</b>{reviewing.po_number && <>  ·  PO: <b>{reviewing.po_number}</b></>}</div>
                </div>
              </div>
              {reviewing.intake_notes && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Notes from client</div>
                  <div className="p-3 bg-amber-50 border-l-4 border-amber-400 rounded-md text-xs italic">"{reviewing.intake_notes}"</div>
                </div>
              )}
              {(reviewing.intake_files || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                    <Paperclip size={11} className="inline mr-1" /> Uploaded files ({reviewing.intake_files.length})
                  </div>
                  <div className="space-y-1">
                    {reviewing.intake_files.map((f, i) => (
                      <a key={i}
                         href={`${API}/public/pending-clients/${reviewing.client_id}/files/${i}`}
                         className="flex items-center justify-between px-3 py-2 border border-slate-200 rounded-md text-xs bg-white hover:bg-slate-50"
                         target="_blank" rel="noreferrer"
                      >
                        <span className="truncate font-mono">{f.filename}</span>
                        <span className="text-slate-400 whitespace-nowrap ml-2">
                          {(f.size / 1024).toFixed(1)} KB <DownloadSimple size={12} className="inline ml-1" />
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-3">
                <Button variant="outline" onClick={() => reject.mutate(reviewing.client_id)} className="flex-1 text-red-700" data-testid="review-reject">
                  <XCircle size={14} className="mr-1" /> Reject
                </Button>
                <Button onClick={() => approve.mutate(reviewing.client_id)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="review-approve">
                  <CheckCircle size={14} className="mr-1" /> Approve & add to roster
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone = "slate" }) {
  const toneCls = { slate: "text-slate-900", amber: "text-amber-700", emerald: "text-emerald-700" }[tone];
  return (
    <div className="card-flat p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
